import { useState } from "react";
import { toast } from "sonner";
import { Users, Radio, LogOut, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader, getAdminToken } from "@/lib/adminAuth";

/* ⚽ PENALTY PARTY — live copy-kick room. Leader (admin) opens the room; every
   one of his kicks fires the same contract on every joined follower's REAL
   account at the same moment. Followers set their own stake + loss cap.
   Honesty rail: every kick is ~50/50 — the leader has NO edge. */
const PenaltyParty = ({ party, accountId, token, isReal, onChanged, onConnect }) => {
  const [stake, setStake] = useState("0.35");
  const [cap, setCap] = useState("40");
  const [busy, setBusy] = useState(false);
  const hasAdmin = !!getAdminToken();
  if (!party) return null;
  const my = party.my;

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const openRoom = () => call(
    () => api.post("/arcade/party/open", { leader_account: accountId, leader_token: token, leader_name: "VTS Leader" }, { headers: authHeader() }),
    "Party is LIVE — your kicks now fire for everyone. Telegram announcement queued (sends unless alerts are paused).");
  const closeRoom = () => call(
    () => api.post("/arcade/party/close", {}, { headers: authHeader() }),
    "Party closed — followers released.");
  const join = () => call(
    () => api.post("/arcade/party/join", { account_id: accountId, token, stake: Number(stake), loss_cap: Number(cap), is_demo: !isReal }),
    "You're in! Every leader kick now fires on your account too.");
  const leave = () => call(
    () => api.post("/arcade/party/leave", { account_id: accountId, token }),
    "You left the party — no more kicks will fire on your account.");

  const feed = (party.kicks || []).slice(0, 5);

  return (
    <div data-testid="penalty-party" className="rounded-lg border border-[#2962FF]/40 bg-[#2962FF]/5 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Users className="w-4 h-4 text-[#2962FF]" />
        <span className="text-[11px] font-black font-mono uppercase tracking-wider text-white">Penalty Party</span>
        {party.open ? (
          <span data-testid="party-live-chip" className="px-2 py-0.5 rounded-full bg-[#089981]/15 text-[#089981] text-[9px] font-black font-mono animate-pulse">
            <Radio className="w-3 h-3 inline mr-1" />LIVE · {party.members} follower{party.members === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full bg-white/5 text-white/40 text-[9px] font-mono">no party right now</span>
        )}
        <span className="ml-auto text-[9px] font-mono text-white/40">every kick is ~50/50 — the leader has no edge</span>
      </div>

      {/* 👑 admin controls */}
      {hasAdmin && !party.open && (
        <button data-testid="party-open-btn" disabled={busy || !accountId} onClick={accountId ? openRoom : onConnect}
          className="px-3 py-2 rounded-md bg-[#2962FF] text-white text-[10px] font-black font-mono hover:bg-[#2962FF]/80 transition-colors disabled:opacity-40">
          <Megaphone className="w-3 h-3 inline mr-1" /> OPEN PARTY — my kicks fire for everyone
        </button>
      )}
      {hasAdmin && party.open && party.is_leader && (
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid="party-leader-banner" className="text-[10px] font-mono text-[#089981] font-bold">
            🎉 You're the leader — every shot below fires for {party.members + 1} account{party.members === 0 ? "" : "s"} at the same moment.
          </span>
          <button data-testid="party-close-btn" disabled={busy} onClick={closeRoom}
            className="px-2.5 py-1 rounded-md bg-[#F23645] text-white text-[10px] font-black font-mono">END PARTY</button>
        </div>
      )}

      {/* 🙋 follower views */}
      {party.open && !party.is_leader && my?.active && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono">
            <span className="text-white/70">joined · ${Number(my.stake).toFixed(2)}/kick · cap ${Number(my.loss_cap).toFixed(2)}</span>
            <span data-testid="party-my-net" className={`font-bold ${(my.net || 0) >= 0 ? "text-[#089981]" : "text-[#F23645]"}`}>
              {(my.net || 0) >= 0 ? "+" : "−"}${Math.abs(my.net || 0).toFixed(2)} · {my.wins || 0}W/{my.losses || 0}L
            </span>
            {(my.hole || 0) > 0.009 && (
              <span data-testid="party-my-recovery" className="px-1.5 py-0.5 rounded bg-[#F7B731]/15 text-[#F7B731] text-[9px] font-bold font-mono">
                🔁 recovering ${Number(my.hole).toFixed(2)} — next kick ~${Math.min(10, Math.max(0.35, Math.ceil((my.hole * 1.2 / 0.95) * 100) / 100)).toFixed(2)} (+20%), back to base after a goal
              </span>
            )}
            <button data-testid="party-leave-btn" disabled={busy} onClick={leave}
              className="px-2 py-1 rounded-md border border-white/20 text-white/60 hover:text-white text-[9px] font-mono transition-colors">
              <LogOut className="w-3 h-3 inline mr-0.5" />LEAVE
            </button>
          </div>
          <p className="text-[9px] font-mono text-white/40">Keep this page open to watch — kicks fire on your account even if you close it. You'll be auto-removed at your loss cap.</p>
        </div>
      )}
      {party.open && !party.is_leader && !my?.active && (
        <div className="space-y-1.5">
          {my?.left_reason && (
            <p data-testid="party-left-reason" className="text-[10px] font-mono text-[#F7B731]">
              You're out of the room ({my.left_reason}){my.left_reason === "loss cap reached" ? " — your cap protected the rest of your balance." : ""} Rejoin below if you want back in.
            </p>
          )}
          {!accountId ? (
            <button onClick={onConnect} className="px-3 py-2 rounded-md bg-[#2962FF] text-white text-[10px] font-black font-mono">CONNECT ACCOUNT TO JOIN</button>
          ) : !isReal ? (
            <p data-testid="party-real-only" className="text-[10px] font-mono text-[#F7B731]">Real accounts only — switch to your real account in the header to join the party.</p>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <label className="block">
                <span className="text-[9px] font-mono uppercase text-white/40">My stake / kick</span>
                <input data-testid="party-stake-input" type="number" min="0.35" max="10" step="0.01" value={stake} onChange={(e) => setStake(e.target.value)}
                  className="block w-24 bg-[#0B0E14] border border-white/15 rounded-md px-2 py-1.5 text-xs font-mono text-white" />
              </label>
              <label className="block">
                <span className="text-[9px] font-mono uppercase text-white/40">Loss cap (auto-exit) · up to $500</span>
                <input data-testid="party-cap-input" type="number" min="0.35" max="500" step="0.5" value={cap} onChange={(e) => setCap(e.target.value)}
                  className="block w-24 bg-[#0B0E14] border border-white/15 rounded-md px-2 py-1.5 text-xs font-mono text-white" />
              </label>
              <button data-testid="party-join-btn" disabled={busy} onClick={join}
                className="px-3 py-2 rounded-md bg-[#089981] text-white text-[10px] font-black font-mono hover:bg-[#067a67] transition-colors disabled:opacity-40">
                JOIN — kick with the leader
              </button>
              <p className="w-full text-[9px] font-mono text-white/40">⚠️ Real money. Wins AND losses mirror the leader. If your kick loses, your next kick auto-raises to win it back +20%, then drops back to your base after a goal — same as manual mode. Only stake what you can afford to lose.</p>
            </div>
          )}
        </div>
      )}

      {/* 📡 live kick feed */}
      {party.open && feed.length > 0 && (
        <div data-testid="party-kick-feed" className="space-y-1">
          {feed.map((k) => (
            <div key={k.seq} className="flex items-center gap-2 text-[10px] font-mono text-white/60">
              <span className="text-white/35">#{k.seq}</span>
              <span className={k.side === "EVEN" ? "text-[#F23645]" : "text-[#089981]"}>{k.side}</span>
              {k.digit != null && <span className="px-1 rounded bg-white/10 text-white/80">{k.digit}</span>}
              <span className={k.leader_won ? "text-[#089981]" : "text-[#F23645]"}>{k.leader_won ? "⚽ GOAL" : "🧤 SAVED"}</span>
              <span className="text-white/35">{k.goals}✓/{k.saves}✗ across the room</span>
              {k.my_profit != null && (
                <span className={`ml-auto font-bold ${k.my_won ? "text-[#089981]" : "text-[#F23645]"}`}>
                  you {k.my_won ? "+" : "−"}${Math.abs(k.my_profit).toFixed(2)}
                </span>
              )}
              {k.my_error && <span className="ml-auto text-[#F7B731]">yours failed: {k.my_error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PenaltyParty;
